import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { Video, Camera, BookOpen, MessageCircle, Sparkles, ArrowDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { StoreButtons } from "./StoreButtons";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1.5">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.05, ease: EASE, delay }}>
      {children}
    </motion.span>
  </span>
);

const FloatCard = ({ icon, label, className, delay, depth, mx, my }) => {
  const x = useTransform(mx, (v) => v * depth);
  const y = useTransform(my, (v) => v * depth);
  return (
    <motion.div
      style={{ x, y }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.8, ease: EASE }}
      className={`absolute hidden items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-xl md:flex ${className}`}
    >
      <span className="text-indril-saffron">{icon}</span>
      <span className="text-sm font-medium text-white/80">{label}</span>
    </motion.div>
  );
};

export const Hero3D = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const mx = useSpring(rawX, { stiffness: 60, damping: 18 });
  const my = useSpring(rawY, { stiffness: 60, damping: 18 });

  const rotateY = useTransform(mx, [-1, 1], [12, -12]);
  const rotateX = useTransform(my, [-1, 1], [-12, 12]);

  const onMove = (e) => {
    if (reduce) return;
    const r = ref.current.getBoundingClientRect();
    rawX.set(((e.clientX - r.left) / r.width - 0.5) * 2);
    rawY.set(((e.clientY - r.top) / r.height - 0.5) * 2);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-screen flex-col items-center justify-center px-6 pt-32 pb-20 text-center"
      data-testid="hero-section"
    >
      <FloatCard icon={<Video className="h-4 w-4" />} label="Short Videos" className="left-[7%] top-[24%]" delay={1.0} depth={22} mx={mx} my={my} />
      <FloatCard icon={<Camera className="h-4 w-4" />} label="Photos" className="right-[8%] top-[28%]" delay={1.15} depth={30} mx={mx} my={my} />
      <FloatCard icon={<BookOpen className="h-4 w-4" />} label="Stories" className="left-[12%] bottom-[24%]" delay={1.3} depth={18} mx={mx} my={my} />
      <FloatCard icon={<MessageCircle className="h-4 w-4" />} label="Private Chat" className="right-[11%] bottom-[26%]" delay={1.45} depth={26} mx={mx} my={my} />

      {/* eyebrow */}
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }} className="mb-9 flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/70 backdrop-blur-xl">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-indril-green" />
        Create. Share. Connect.
      </motion.div>

      {/* 3D logo */}
      <motion.div style={{ perspective: 900 }} className="relative mb-8">
        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indril-saffron/35 blur-[90px]" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-56 w-56 -translate-x-[62%] -translate-y-1/2 rounded-full bg-indril-pink/25 blur-[80px]" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-56 w-56 -translate-x-[38%] -translate-y-[42%] rounded-full bg-indril-blue/20 blur-[80px]" />
        <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} initial={{ opacity: 0, scale: 0.7, filter: "blur(14px)" }} animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} transition={{ delay: 0.3, duration: 1.1, ease: EASE }}>
          <Logo variant="mark" className="w-32 drop-shadow-2xl sm:w-40" />
        </motion.div>
      </motion.div>

      <h1 className="font-display text-4xl font-semibold leading-[0.98] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-heading">
        <Line delay={0.5}>IND RIL</Line>
        <Line delay={0.62}><span className="text-tricolor">India's Own Social Platform</span></Line>
      </h1>

      <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8, ease: EASE }} className="mt-7 max-w-xl text-base text-white/60 sm:text-lg">
        A fast, secure and creator-first platform built for India. Share videos, photos, stories and conversations — all in one place.
      </motion.p>

      <motion.div id="download" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8, ease: EASE }} className="mt-10 flex scroll-mt-28 flex-col items-center gap-4">
        <StoreButtons size="lg" />
        <button
          onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
          className="group mt-1 inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-white"
          data-testid="hero-explore-features"
        >
          <Sparkles className="h-4 w-4 text-indril-saffron" /> Explore Features
          <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
        </button>
      </motion.div>
    </section>
  );
};
